import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sun, Moon, Activity, CheckCircle } from "lucide-react";
import BallpitBackground from "@/components/BallpitBackground";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { getDiseaseBySlug } from "@/data/dentalDiseases";

const LOGO_URL = "/exoi_dental_logo.svg";

function PainLevelBar({ level, label }) {
  const clamped = Math.min(10, Math.max(0, Number(level) || 0));
  const pct = (clamped / 10) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-500" aria-hidden />
          Typical pain level
        </span>
        <span className="text-sm font-semibold text-cyan-600 dark:text-cyan-400 tabular-nums">
          {clamped}/10
        </span>
      </div>
      <div
        className="h-3 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden border border-slate-200 dark:border-cyan-500/20"
        role="img"
        aria-label={`Pain level ${clamped} out of 10`}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-amber-400 to-rose-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {label && (
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      )}
    </div>
  );
}

export default function DiseaseDetailPage() {
  const { slug } = useParams();
  const { isDark, toggleTheme } = useTheme();

  const disease = useMemo(() => getDiseaseBySlug(slug), [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!disease) {
    return (
      <div className="App min-h-screen relative text-slate-900 dark:text-white">
        <BallpitBackground />
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 pt-24 pb-16">
          <div className="glass-card rounded-2xl p-10 max-w-md text-center border border-slate-200 dark:border-cyan-500/20 bg-white/90 dark:bg-slate-900/70">
            <h1 className="text-2xl font-bold mb-2">Page not found</h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              We couldn&apos;t find a guide for that condition.
            </p>
            <Button asChild className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
              <Link to="/#services">Back to services</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen relative text-slate-900 dark:text-white">
      <BallpitBackground />
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Match landing header style/height so it doesn't look like it changes */} 
        <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-24 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-cyan-500/30 text-cyan-600 hover:bg-cyan-50"
                  asChild
                >
                  <Link to="/#services" className="flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="hidden sm:inline">Services</span>
                  </Link>
                </Button>
                <Link to="/" className="flex items-center gap-2 min-w-0">
                  <img src={LOGO_URL} alt="Extol Dental" className="h-20 w-auto object-contain" />
                </Link>
              </div>

              <button
                type="button"
                onClick={toggleTheme}
                className="p-2 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-600 hover:bg-cyan-100 transition-all"
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 disease-detail-page max-w-5xl w-full mx-auto px-4 sm:px-6 pt-32 pb-10 md:pb-14">
          <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="glass-card rounded-3xl border border-slate-200 dark:border-cyan-500/20 bg-white/90 dark:bg-slate-900/60 backdrop-blur-md overflow-hidden shadow-xl shadow-slate-200/50 dark:shadow-cyan-950/20"
          >
            <div className="p-6 md:p-10">
              <div className="flex flex-wrap items-start gap-4 mb-6">
                <span className="text-5xl" aria-hidden>
                  {typeof disease.icon === "string" && (/^https?:/i.test(disease.icon) || disease.icon.startsWith("/")) ? (
                    <img
                      src={disease.icon}
                      alt={`${disease.title} icon`}
                      className="h-16 w-16 object-contain"
                      loading="lazy"
                    />
                  ) : (
                    disease.icon
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-cyan-600 dark:text-cyan-400 mb-1">
                    Patient guide
                  </p>
                  <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                    {disease.title}
                  </h1>
                  <p className="text-lg text-slate-600 dark:text-slate-300 mt-2">
                    {disease.description}
                  </p>
                </div>
              </div>

              <div className="mb-10 p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-white/5">
                <PainLevelBar level={disease.painLevel} label={disease.painLabel} />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-10">
                {(disease.images || []).map((img, i) => (
                  <figure
                    key={i}
                    className="rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 shadow-md aspect-[4/3] bg-slate-100 dark:bg-slate-800"
                  >
                    <img
                      src={img.url}
                      alt={img.alt || disease.title}
                      className="w-full h-full object-cover saturate-[1.15] contrast-[1.03]"
                      loading={i === 0 ? "eager" : "lazy"}
                    />
                  </figure>
                ))}
              </div>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                  Common symptoms
                </h2>
                <ul className="space-y-3">
                  {(disease.symptoms || []).map((s, i) => (
                    <li key={i} className="flex gap-3 text-slate-700 dark:text-slate-300">
                      <CheckCircle className="w-5 h-5 text-cyan-500 shrink-0 mt-0.5" aria-hidden />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                  About this condition
                </h2>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                  {disease.details}
                </p>
              </section>

              <div className="mt-10 pt-8 border-t border-slate-200 dark:border-white/10 flex flex-wrap gap-4">
                <Button
                  asChild
                  className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                >
                  <Link to="/#services">All services</Link>
                </Button>
                <Button variant="outline" asChild className="rounded-full border-cyan-500/40">
                  <Link to="/">Home</Link>
                </Button>
              </div>
            </div>
          </motion.article>
        </main>
      </div>
    </div>
  );
}
